import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ApiEndpointEnum } from '@app/constants/api-endpoint.enum';
import { RouteEnum } from '@app/constants/route.enum';
import { UtilityEnum } from '@app/constants/utility.enum';
import {
  AuthRefreshCoordinator,
  isUnauthorizedAuthError,
} from '@app/services/auth/auth-refresh.coordinator';
import { AppStore } from '@app/stores/app.store';
import { catchError, switchMap, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const appStore = inject(AppStore);
  const router = inject(Router);
  const refreshCoordinator = inject(AuthRefreshCoordinator);

  let token = '';
  const headersConfig: Record<string, string> = {
    companyID: 'ALL',
    'x-encryption': 'true',
  };
  if (!(req.body instanceof FormData)) {
    headersConfig['Content-Type'] = 'application/json';
  }
  try {
    const authToken = appStore.authToken();
    if (authToken) {
      token = authToken;
    }
  } catch {
    refreshCoordinator.clearSession();
    return throwError(() => new Error('Auth state unavailable'));
  }
  const isRefreshRequest = req.url.includes(ApiEndpointEnum.REFRESH_TOKEN);
  // Refresh must not send expired access JWT (public route).
  if (token && !isRefreshRequest) {
    headersConfig['Authorization'] = 'Bearer ' + token;
  }
  const headers = new HttpHeaders(headersConfig);
  const cloned = req.clone({ headers });

  return next(cloned).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error?.status === 503 && error?.error?.code === UtilityEnum.SERVICE_UNAVAILABLE) {
        refreshCoordinator.clearSession();
        router.navigate([RouteEnum.MAINTENANCE]);
        return throwError(() => error);
      }

      if (!isUnauthorizedAuthError(error) || isRefreshRequest) {
        return throwError(() => error);
      }

      return refreshCoordinator.refreshAccessToken().pipe(
        catchError(() => {
          refreshCoordinator.clearSession();
          return throwError(() => error);
        }),
        switchMap(() => {
          const freshToken = appStore.authToken();
          if (!freshToken) {
            refreshCoordinator.clearSession();
            return throwError(() => error);
          }
          const retryHeaders = cloned.headers.set(
            'Authorization',
            'Bearer ' + freshToken,
          );
          return next(cloned.clone({ headers: retryHeaders }));
        }),
      );
    }),
  );
};
